import { t as tr } from '../i18n.js'
import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'
import { api, ApiError, type DirectoryUser, type PortfolioDetail, type Project, type ProjectRole } from '../api.js'
import { fmt } from './Account.js'
import { atLeast, useAuth } from '../auth.js'
import { Section, jumpToSection } from '../section.js'
import { PortfolioTabs } from './PortfolioTabs.js'
import { PortfolioOverviewPanel, Swatch } from './PortfolioDashboard.js'
import './portfolio.css'

const PORTFOLIO_ROLES: ProjectRole[] = ['admin', 'member', 'viewer']
const roleLabel = (r: ProjectRole) => r === 'admin' ? tr("portfolio administrator") : r

export function PortfolioDetailPage() {
  const { portfolioId = '' } = useParams()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['portfolio', portfolioId], queryFn: () => api<PortfolioDetail>(`/portfolios/${portfolioId}`), retry: false })
  const invalidate = () => {
    for (const k of [['portfolio', portfolioId], ['portfolios'], ['portfolio-overview', portfolioId], ['projects']]) void qc.invalidateQueries({ queryKey: k })
  }

  if (q.isPending) return <p className="muted">{tr("Loading…")}</p>
  if (q.isError) return <p className="bad">{tr("Portfolio not found, or you do not have access.")} <Link to="/portfolios">{tr("Back to portfolios")}</Link></p>
  const { portfolio: p, projects, hiddenProjects, permissions } = q.data
  const n = projects.length

  return (
    <div className="boardpage">
      <p className="crumbs"><Link to="/portfolios">{tr("Portfolios")}</Link> / {p.name}</p>
      <PortfolioTabs portfolioId={p.id} active="overview" />
      <div className="row between pfhead">
        <div>
          <h1>{p.name} {p.archivedAt && <span className="tag">{tr("archived")}</span>}</h1>
          <p className="muted">
            {p.ownerName ? <>{tr("Managed by")} <strong>{p.ownerName}</strong> · </> : null}
            {n === 1 ? tr("1 project") : tr("{n} projects", { n })}
            {hiddenProjects > 0 && <> · <span data-tip={tr("Private projects filed here that only their owners can see")}>{hiddenProjects === 1 ? tr("1 private project hidden") : tr("{n} private projects hidden", { n: hiddenProjects })}</span></>}
          </p>
        </div>
      </div>
      <Section id="pf-overview" title={tr("Overview")} subtitle={tr("description, dashboard")} defaultOpen>
        {p.description && <p style={{ whiteSpace: 'pre-wrap' }}>{p.description}</p>}
        <PortfolioOverviewPanel portfolioId={p.id} />
      </Section>
      <Section id="pf-projects" title={tr("Projects")} subtitle={`${n}`} defaultOpen>
        <ProjectsPanel d={q.data} onChange={invalidate} />
      </Section>
      <Section id="pf-members" title={tr("Members")} subtitle={`${q.data.members.length} people`}>
        <MembersPanel d={q.data} onChange={invalidate} />
      </Section>
      {permissions.update && <Section id="pf-settings" title={tr("Settings")} subtitle={tr("name, description, portfolio manager, archive")}><SettingsPanel d={q.data} onChange={invalidate} /></Section>}
    </div>
  )
}

function useErr() {
  const [err, setErr] = useState<string | null>(null)
  const onError = (e: unknown) => setErr(e instanceof ApiError ? e.message : tr("Request failed"))
  return { err, setErr, onError }
}

function ProjectsPanel({ d, onChange }: { d: PortfolioDetail; onChange: () => void }) {
  const { portfolio: p, projects, permissions } = d
  const { user } = useAuth()
  const { err, setErr, onError } = useErr()
  const all = useQuery({ queryKey: ['projects', false], queryFn: () => api<{ projects: Project[] }>('/projects'), enabled: permissions.manageProjects })
  const [pick, setPick] = useState('')
  const add = useMutation({ mutationFn: (pid: string) => api(`/portfolios/${p.id}/projects/${pid}`, { method: 'PUT' }), onSuccess: () => { setPick(''); onChange() }, onError })
  const remove = useMutation({ mutationFn: (pid: string) => api(`/portfolios/${p.id}/projects/${pid}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  // Filing needs update on the project: a project admin, or a global manager+.
  const candidates = (all.data?.projects ?? []).filter((x) => x.portfolioId === null && !x.archivedAt && (x.myRole === 'admin' || atLeast(user, 'manager')))

  return (
    <div className="stack">
      {projects.length === 0 && <p className="muted small">{tr("No projects filed here yet.")}</p>}
      {projects.length > 0 && (
        <table className="table pftable">
          <thead><tr><th></th><th>{tr("Code")}</th><th>{tr("Name")}</th><th>{tr("Status")}</th><th>{tr("Your role")}</th><th></th>{permissions.manageProjects && <th></th>}</tr></thead>
          <tbody>
            {projects.map((x, i) => (
              <tr key={x.id} className={x.archivedAt ? 'muted' : ''}>
                <td><Swatch color={x.color} i={i} /></td>
                <td className="code"><Link to={`/projects/${x.id}`} data-tip={tr("Open the project")}><strong>{x.code}</strong></Link></td>
                <td><Link to={`/projects/${x.id}`}>{x.name}</Link>{x.isPrivate && <span className="tag">{tr("private")}</span>}{!x.permissions.manageTasks && <span className="tag" data-tip={tr("You can see this project's tasks here but not change them")}>{tr("read-only")}</span>}</td>
                <td>{x.status.replace('_', ' ')}</td>
                <td>{x.myRole ? (x.myRole === 'admin' ? tr("project administrator") : x.myRole) : <span className="muted">—</span>}</td>
                <td className="small"><Link to={`/projects/${x.id}/board`} data-tip={tr("Open the Kanban board")}>{tr("Board")}</Link> · <Link to={`/projects/${x.id}/gantt`} data-tip={tr("Open the Gantt chart")}>{tr("Gantt")}</Link></td>
                {permissions.manageProjects && <td><button className="link danger" disabled={remove.isPending} data-tip={tr("Take the project out of this portfolio (the project itself is unchanged)")} onClick={() => { setErr(null); remove.mutate(x.id) }}>{tr("Remove")}</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {permissions.manageProjects && (
        <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); if (pick) add.mutate(pick) }}>
          <select value={pick} onChange={(e) => setPick(e.target.value)} required style={{ flex: 1 }} data-tip={tr("Projects not yet in a portfolio that you may update")}>
            <option value="">{tr("Add project…")}</option>
            {candidates.map((x) => <option key={x.id} value={x.id}>{x.code} {x.name}</option>)}
          </select>
          <button className="button" disabled={!pick || add.isPending}>{tr("Add")}</button>
        </form>
      )}
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function MembersPanel({ d, onChange }: { d: PortfolioDetail; onChange: () => void }) {
  const { portfolio: p, members, permissions } = d
  const { err, setErr, onError } = useErr()
  const users = useQuery({ queryKey: ['users', false], queryFn: () => api<{ users: DirectoryUser[] }>('/users'), enabled: permissions.manageMembers })
  const [pick, setPick] = useState('')
  const [role, setRole] = useState<ProjectRole>('member')
  const put = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) => api(`/portfolios/${p.id}/members/${userId}`, { method: 'PUT', json: { role } }),
    onSuccess: () => { setPick(''); onChange() }, onError,
  })
  const del = useMutation({ mutationFn: (userId: string) => api(`/portfolios/${p.id}/members/${userId}`, { method: 'DELETE' }), onSuccess: onChange, onError })
  const memberIds = new Set(members.map((m) => m.userId))
  const candidates = (users.data?.users ?? []).filter((u) => !memberIds.has(u.id))

  return (
    <div className="stack">
      <p className="small muted">{tr("Portfolio manager")}: <strong>{p.ownerName ?? '—'}</strong>{permissions.update && <> · <button type="button" className="link plain small" onClick={() => jumpToSection('pf-settings')}>{tr("change in Settings")}</button></>}</p>
      {members.length === 0 && <p className="muted small">{tr("No members yet.")}</p>}
      {members.length > 0 && (
        <table className="table">
          <thead><tr><th>{tr("Name")}</th><th>{tr("Portfolio role")}</th><th>{tr("Since")}</th>{permissions.manageMembers && <th></th>}</tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.userId}>
                <td><strong>{m.displayName}</strong><br /><span className="small muted">{m.email}</span></td>
                <td>{permissions.manageMembers
                  ? <select value={m.role} onChange={(e) => { setErr(null); put.mutate({ userId: m.userId, role: e.target.value as ProjectRole }) }}>{PORTFOLIO_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</select>
                  : roleLabel(m.role)}</td>
                <td className="small">{fmt(m.addedAt)}</td>
                {permissions.manageMembers && <td><button className="link danger" onClick={() => { setErr(null); del.mutate(m.userId) }}>{tr("Remove")}</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {permissions.manageMembers && (
        <form className="row" onSubmit={(e: FormEvent) => { e.preventDefault(); setErr(null); if (pick) put.mutate({ userId: pick, role }) }}>
          <select value={pick} onChange={(e) => setPick(e.target.value)} required style={{ flex: 1 }}>
            <option value="">{tr("Add a person…")}</option>
            {candidates.map((u) => <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>)}
          </select>
          <select value={role} onChange={(e) => setRole(e.target.value as ProjectRole)}>{PORTFOLIO_ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</select>
          <button className="button" disabled={!pick}>{tr("Add")}</button>
        </form>
      )}
      {err && <p className="bad">{err}</p>}
    </div>
  )
}

function SettingsPanel({ d, onChange }: { d: PortfolioDetail; onChange: () => void }) {
  const { portfolio: p } = d
  const { err, setErr, onError } = useErr()
  const users = useQuery({ queryKey: ['users', false], queryFn: () => api<{ users: DirectoryUser[] }>('/users') })
  const [name, setName] = useState(p.name)
  const [description, setDescription] = useState(p.description ?? '')
  const [ownerId, setOwnerId] = useState(p.ownerId ?? '')
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: () => api(`/portfolios/${p.id}`, { method: 'PATCH', json: { name, description: description || null, ...(ownerId && ownerId !== p.ownerId ? { ownerId } : {}) } }),
    onSuccess: () => { setSaved(true); onChange() }, onError,
  })
  const archive = useMutation({ mutationFn: (archived: boolean) => api(`/portfolios/${p.id}`, { method: 'PATCH', json: { archived } }), onSuccess: onChange, onError })
  return (
    <form className="stack" onSubmit={(e) => { e.preventDefault(); setErr(null); setSaved(false); save.mutate() }}>
      <label>{tr("Name")}<input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} /></label>
      <label>{tr("Description")}<textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
      <label>{tr("Portfolio manager")}
        <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} data-tip={tr("The person accountable for the portfolio; always a portfolio administrator")}>
          {!ownerId && <option value="">—</option>}
          {(users.data?.users ?? []).filter((u) => !u.deactivatedAt || u.id === p.ownerId).map((u) => <option key={u.id} value={u.id}>{u.displayName} ({u.email})</option>)}
          {ownerId && !users.data?.users.some((u) => u.id === ownerId) && <option value={ownerId}>{p.ownerName ?? ownerId}</option>}
        </select>
      </label>
      {err && <p className="bad">{err}</p>}
      {saved && <p className="good">{tr("Saved.")}</p>}
      <div className="row">
        <button className="button" disabled={save.isPending}>{tr("Save")}</button>
        {p.archivedAt
          ? <button type="button" className="button" onClick={() => archive.mutate(false)}>{tr("Unarchive")}</button>
          : <button type="button" className="button danger" onClick={() => { if (confirm(tr("Archive this portfolio? Its projects are unchanged; the portfolio drops out of the default lists."))) archive.mutate(true) }}>{tr("Archive")}</button>}
      </div>
    </form>
  )
}
